import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import { api } from "../../api";
import { AMetric, APageHead } from "../adminKit";

const COLORS = ["#3B82F6", "#8B5CF6", "#06b6d4", "#a78bfa"];
const tip = { background: "#12121a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 };
const RANGES = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["year", "This year"]];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [rng, setRng] = useState("30d");

  useEffect(() => { api.get(`/admin/dashboard?rng=${rng}`).then(({ data }) => setData(data)); }, [rng]);
  if (!data) return <div className="text-slate-500">Loading dashboard…</div>;
  const m = data.metrics;

  return (
    <div className="space-y-6">
      <APageHead title="Admin Dashboard" subtitle="Real-time platform metrics from live data."
        action={
          <div className="flex gap-1.5 flex-wrap">
            {RANGES.map(([v, l]) => (
              <button key={v} onClick={() => setRng(v)} data-testid={`range-${v}`}
                className={`rounded-full px-3 py-1.5 text-xs ${rng === v ? "cc-gradient text-white" : "border border-white/10 text-slate-400 cc-nav-item"}`}>{l}</button>
            ))}
          </div>
        } />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <AMetric label="Total Users" value={m.total_users} sub={`+${m.new_today} today`} tone="blue" />
        <AMetric label="New (7d / 30d)" value={`${m.new_week} / ${m.new_month}`} sub="new signups" tone="purple" />
        <AMetric label="MRR" value={`$${m.mrr}`} sub={`ARR ~$${m.arr}`} tone="green" />
        <AMetric label="New Revenue (30d)" value={`$${m.new_revenue_month}`} sub={`${m.paid_subscriptions} paid subs`} tone="green" />
        <AMetric label="Active / Suspended" value={`${m.active_users} / ${m.suspended_users}`} tone="amber" />
        <AMetric label="Free / Pro / Biz / Ent" value={`${m.free_users}/${m.pro_users}/${m.business_users}/${m.enterprise_users}`} />
        <AMetric label="Canceled Subs" value={m.canceled_subscriptions} tone="red" />
        <AMetric label="Open Support" value={m.open_support} tone="amber" />
        <AMetric label="Reports Analyzed" value={m.reports_analyzed} tone="blue" />
        <AMetric label="Disputes Generated" value={m.disputes_generated} tone="purple" />
        <AMetric label="Vendors Tracked" value={m.vendors_tracked} />
        <AMetric label="Documents" value={m.documents_uploaded} />
        <AMetric label="AI Conversations" value={m.ai_conversations} tone="blue" />
        <AMetric label="AI Messages" value={m.ai_messages} tone="purple" />
        <AMetric label="Funding Assessments" value={m.funding_assessments} tone="green" />
        <AMetric label="Paid Subscriptions" value={m.paid_subscriptions} tone="green" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="cc-card rounded-2xl p-5 lg:col-span-2">
          <h3 className="cc-heading text-base font-500 text-white mb-4">User & Revenue Growth (14d)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.series}>
              <defs>
                <linearGradient id="gu" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
                <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.5} /><stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tip} />
              <Area type="monotone" dataKey="users" stroke="#3B82F6" fill="url(#gu)" strokeWidth={2} />
              <Area type="monotone" dataKey="revenue" stroke="#8B5CF6" fill="url(#gr)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="cc-card rounded-2xl p-5">
          <h3 className="cc-heading text-base font-500 text-white mb-4">Plan Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.plan_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                {data.plan_distribution.map((d, i) => <Cell key={d.name || i} fill={COLORS[i % COLORS.length]} stroke="none" />)}
              </Pie>
              <Tooltip contentStyle={tip} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 justify-center mt-1">
            {data.plan_distribution.map((p, i) => <span key={p.name} className="text-xs text-slate-400 capitalize flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{p.name} ({p.value})</span>)}
          </div>
        </div>
      </div>

      <div className="cc-card rounded-2xl p-5">
        <h3 className="cc-heading text-base font-500 text-white mb-4">Recent Administrative Activity</h3>
        <div className="space-y-2">
          {data.recent_admin.length === 0 && <p className="text-sm text-slate-500">No recent admin activity.</p>}
          {data.recent_admin.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-sm border-b border-white/[0.03] pb-2">
              <span className="text-slate-300 font-mono text-xs">{a.action}</span>
              <span className="text-slate-600 text-xs">{new Date(a.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
